import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { SEO, Reveal, Eyebrow } from "@/components/motion";

export default function Services() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="services-page">
      <SEO
        title="Our Services | Home, Deep, Property & Business Cleaning in London | HOMELARA"
        description="Thoughtful cleaning for homes, properties and businesses across London Zones 1 & 2. Home cleaning, deep cleaning, Airbnb & property turnover cleaning and business cleaning."
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-32 sm:pt-44 pb-16">
        <Reveal>
          <Eyebrow>Our Services</Eyebrow>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink">
            Thoughtful cleaning for every kind of space.
          </h1>
          <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-body">
            Thoughtful cleaning for homes, properties and businesses - giving you more time to enjoy what matters.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 sm:pb-32">
        <div className="space-y-16 sm:space-y-24">
          {services.map((s, i) => (
            <Reveal key={s.slug}>
              <article
                data-testid={`services-row-${s.slug}`}
                className={`grid items-center gap-8 lg:grid-cols-2 ${
                  i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="img-frame group aspect-[4/3] overflow-hidden rounded-3xl">
                  <img
                    src={s.image}
                    alt={`${s.name} in London Zones 1 & 2 - HOMELARA professional cleaning`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="lg:px-6">
                  <span className="font-mono text-xs tracking-[0.3em] text-sage-dark">0{i + 1}</span>
                  <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight text-ink">{s.name}</h2>
                  <p className="mt-2 font-serif text-lg italic text-sage-dark">{s.tagline}</p>
                  <p className="mt-4 max-w-lg text-base leading-relaxed text-body">{s.short_desc}</p>
                  <div className="mt-7 flex flex-wrap gap-4">
                    <Link
                      to={`/services/${s.slug}`}
                      data-testid={`services-view-details-${s.slug}`}
                      className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.18em] text-ink transition-all duration-300 hover:border-blue hover:text-blue hover:-translate-y-0.5"
                    >
                      View details <ArrowUpRight size={14} />
                    </Link>
                    <Link
                      to={`/book?service=${s.slug}`}
                      data-testid={`services-book-${s.slug}`}
                      className="inline-flex items-center gap-2 rounded-full bg-blue px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.18em] text-canvas transition-all duration-300 hover:bg-blue-hover hover:-translate-y-0.5"
                    >
                      Book a Clean
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
